<template>
  <div>
    <v-btn
      text
      :loading="reportInProgress"
      class="info2 white--text"
      @click="openReportDialog"
    >
      {{ $i18n.t('$l.api.reportButton') }}
      <v-icon
        dark
        right
      >
        mdi-file-document
      </v-icon>
    </v-btn>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
    >
      {{ snackbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click.stop="snackbar = false"
        >
          {{ $i18n.t('$l.app.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <basic-dialog
      v-model="reportDialog"
      title="$l.api.subjects.subjectReport"
      save-button
      save-button-label="$l.app.confirm"
      max-width="900"
      :loading-save-button="reportInProgress"
      @close-dialog="closeReportDialog"
      @save-dialog="generateSubjectReport()"
    >
      <v-container>
        <v-alert
          dense
          text
          type="info"
          class="mb-4"
        >
          {{ $i18n.t('$l.api.subjects.subjectReportDescription') }}
        </v-alert>

        <v-combobox
          v-model="reportParams.apiaffiliate"
          dense
          multiple
          chips
          clearable
          deletable-chips
          :disabled="apiUserAffiliatesLoading"
          :loading="apiUserAffiliatesLoading"
          :items="apiUserAffiliateItems"
          :label="$i18n.t('$l.api.apiUserAffiliate')"
        />

        <v-checkbox
          v-model="reportParams.exclude"
          class="ma-0 pa-0"
          :label="$i18n.t('$l.api.subjects.excludeSelectedAffiliates')"
        />

        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-menu
              ref="dateFromMenu"
              v-model="dateFromMenu"
              :close-on-content-click="false"
              :return-value.sync="reportParams.dateFrom"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  ref="dateFromField"
                  v-model="reportParams.dateFrom"
                  :label="$i18n.t('$l.apicheck.dateFrom')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  dense
                  :rules="[requiredDateRule]"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="reportParams.dateFrom"
                no-title
                scrollable
                :locale="$i18n.locale"
              >
                <v-spacer />
                <v-btn
                  text
                  color="primary"
                  @click="dateFromMenuCancel"
                >
                  {{ $t('$l.app.cancel') }}
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="dateFromMenuOk"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <v-menu
              ref="dateToMenu"
              v-model="dateToMenu"
              :close-on-content-click="false"
              :return-value.sync="reportParams.dateTo"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  ref="dateToField"
                  v-model="reportParams.dateTo"
                  :label="$i18n.t('$l.apicheck.dateTo')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  dense
                  :rules="[requiredDateRule]"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="reportParams.dateTo"
                no-title
                scrollable
                :locale="$i18n.locale"
              >
                <v-spacer />
                <v-btn
                  text
                  color="primary"
                  @click="dateToMenuCancel"
                >
                  {{ $t('$l.app.cancel') }}
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="dateToMenuOk"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-container>
    </basic-dialog>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'

export default {
  name: 'GenerateSubjectReport',
  data () {
    return {
      reportInProgress: false,
      reportDialog: false,
      snackbar: false,
      snackbarColor: 'success',
      snackbarText: '',
      apiUserAffiliateItems: [],
      apiUserAffiliatesLoading: true,
      dateFromMenu: false,
      dateToMenu: false,
      reportValidationStarted: false,
      reportParams: {
        apiaffiliate: [],
        exclude: false,
        dateFrom: '',
        dateTo: ''
      }
    }
  },
  created () {
    if (this.$store.state.user.isAuthenticated) {
      this.loadApiAffiliateItems()
    }
  },
  methods: {
    openReportDialog () {
      this.reportDialog = true
      this.resetDateValidation()
    },
    closeReportDialog () {
      this.reportDialog = false
      this.resetDateValidation()
    },
    generateSubjectReport () {
      this.reportValidationStarted = true

      if (!this.validateDateFields()) {
        this.snackbarColor = 'error'
        this.snackbarText = this.$i18n.t('$l.api.subjects.subjectReportDateRangeRequired')
        this.snackbar = true
        return
      }

      this.reportInProgress = true

      const payload = JSON.parse(JSON.stringify(this.reportParams))

      if (payload.dateFrom !== '') {
        payload.dateFrom = new Date(payload.dateFrom + 'T00:00:00').toISOString()
      }
      if (payload.dateTo !== '') {
        const dateTo = new Date(payload.dateTo + 'T00:00:00')
        dateTo.setDate(dateTo.getDate() + 1)
        payload.dateTo = dateTo.toISOString()
      }

      this.$xapi.post('apiSubject/subjectReport', payload, { responseType: 'blob' })
        .then(r => {
          saveAs(new Blob([r.data], { type: 'text/csv;charset=utf-8' }), this.createReportFileName())
          this.snackbarColor = 'success'
          this.snackbarText = this.$i18n.t('$l.api.subjects.subjectReportGenerated')
          this.snackbar = true
          this.closeReportDialog()
        })
        .catch(e => {
          this.snackbarColor = 'error'
          this.snackbarText = this.$i18n.t('$l.api.generatingReportFailed')
          this.snackbar = true
        })
        .finally(() => {
          this.reportInProgress = false
        })
    },
    createReportFileName () {
      const reportPrefix = this.getReportFilePrefix()
      const dateRange = this.getReportFileDateRange()
      return reportPrefix + '_' + dateRange + '.csv'
    },
    getReportFilePrefix () {
      if (!this.reportParams.exclude && this.reportParams.apiaffiliate.length === 1) {
        return this.sanitizeFileNamePart(this.reportParams.apiaffiliate[0])
      }
      return 'Subjekty'
    },
    getReportFileDateRange () {
      const dateFrom = this.formatFileDate(this.reportParams.dateFrom)
      const dateTo = this.formatFileDate(this.reportParams.dateTo)
      return dateFrom === dateTo ? dateFrom : dateFrom + '-' + dateTo
    },
    formatFileDate (value) {
      const parts = value.split('-')
      return parts[2] + '_' + parts[1] + '_' + parts[0]
    },
    sanitizeFileNamePart (value) {
      return String(value)
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '') || 'Subjekty'
    },
    loadApiAffiliateItems () {
      this.$xapi.get('apiCheck/getApiAffiliate')
        .then(r => {
          this.apiUserAffiliateItems = r.data
        })
        .catch(e => {
        })
        .finally(() => {
          this.apiUserAffiliatesLoading = false
        })
    },
    requiredDateRule (value) {
      return !!value || this.$i18n.t('$l.app.required')
    },
    validateDateFields () {
      const dateFromValid = this.$refs.dateFromField ? this.$refs.dateFromField.validate() : false
      const dateToValid = this.$refs.dateToField ? this.$refs.dateToField.validate() : false
      return dateFromValid && dateToValid
    },
    resetDateValidation () {
      this.reportValidationStarted = false
      this.$nextTick(() => {
        if (this.$refs.dateFromField) {
          this.$refs.dateFromField.resetValidation()
        }
        if (this.$refs.dateToField) {
          this.$refs.dateToField.resetValidation()
        }
      })
    },
    refreshDateValidation () {
      if (this.reportValidationStarted) {
        this.validateDateFields()
      }
    },
    dateFromMenuOk () {
      if (this.$refs.dateFromMenu) {
        this.$refs.dateFromMenu.save(this.reportParams.dateFrom)
      }
      this.refreshDateValidation()
      this.dateFromMenu = false
    },
    dateFromMenuCancel () {
      if (this.$refs.dateFromMenu) {
        this.reportParams.dateFrom = ''
        this.$refs.dateFromMenu.save('')
      }
      this.refreshDateValidation()
      this.dateFromMenu = false
    },
    dateToMenuOk () {
      if (this.$refs.dateToMenu) {
        this.$refs.dateToMenu.save(this.reportParams.dateTo)
      }
      this.refreshDateValidation()
      this.dateToMenu = false
    },
    dateToMenuCancel () {
      if (this.$refs.dateToMenu) {
        this.reportParams.dateTo = ''
        this.$refs.dateToMenu.save('')
      }
      this.refreshDateValidation()
      this.dateToMenu = false
    }
  }
}
</script>
